import {
  InclusionMethod as LegacyInclusionMethod,
  ProductAttributeFilter,
  ProductCategoryFilter,
} from "../../../../legacy/types/companyFilter";
import { SpogCompany } from "../../../../legacy/types/SpogCompany";
import {
  InclusionMethod,
  type IntentFilter,
  type LocationFilter,
} from "../../../../generated/company-search";
import type {
  InstallFilters,
  IntentFilters,
} from "../../../../store/companyPageStore";
import { getUserScopes } from "../../../../utils/auth";
import {
  COMPANY_PROFILE_EXPORT_PARAMETERS_VERSION,
  EXPORT_FORMAT_XLSX,
} from "./constants";
import { CompanyExportInstallParams, CompanyExportParams } from "./types";

export function transformInstallFiltersToInstallExportParams(
  filters: InstallFilters
): CompanyExportInstallParams {
  const products = transformIdsToInclusionFilterList(filters.productIds);
  const vendors = transformIdsToInclusionFilterList(filters.vendorIds);
  const productAttributes = transformIdsToSingleIdFilter(
    filters.productAttributeIds
  );
  const productCategories = transformIdsToSingleIdFilter(
    filters.productCategoryIds
  );

  return {
    countryAlpha2s: filters.countryAlpha2s,
    products,
    vendors,
    productAttributes: productAttributes as Pick<ProductAttributeFilter, "ids">,
    productCategories: productCategories as Pick<ProductCategoryFilter, "ids">,
  };
}

export function transformIntentFiltersToIntentExportParams(
  filters: IntentFilters
): IntentFilter {
  const hasLocation =
    filters.locationCountryAlpha2s.length > 0 ||
    filters.locationStateIds.length > 0 ||
    filters.locationRegionNames.length > 0;

  const location: LocationFilter = hasLocation
    ? {
        countryAlpha2s: filters.locationCountryAlpha2s,
        stateIds: filters.locationStateIds,
        regionNames: filters.locationRegionNames,
      }
    : ({} as LocationFilter);

  return {
    buyersJourneyIds: filters.buyersJourneyIds,
    contextTypeIds: filters.contextTypeIds,
    location,
    topics: {
      ids: filters.topicIds,
      inclusionMethod: InclusionMethod.ANY_PRESENT,
    },
  };
}

export function transformCompanyPageFiltersToExportParams(
  userEmail: string,
  company: SpogCompany,
  companyPageFilters: {
    installFilters: InstallFilters;
    intentFilters: IntentFilters;
  }
): CompanyExportParams {
  return {
    exportType: "companyProfile",
    format: EXPORT_FORMAT_XLSX,
    profile: { email: userEmail },
    scope: getUserScopes(),
    frontendDomain: window.location.origin,
    params: {
      hgId: company.hgId,
      version: COMPANY_PROFILE_EXPORT_PARAMETERS_VERSION,
      title: company.name,
      duns: company.duns,
      includeInstallCorporateDescendants:
        companyPageFilters.installFilters.includeCorporateDescendants,
      includeIntentCorporateDescendants:
        companyPageFilters.intentFilters.includeCorporateDescendants,
      enableUseHermesApi: true,
      exportFaiEnabled: true,
      searchParams: {
        filters: {
          version: 0,
          ...transformInstallFiltersToInstallExportParams(
            companyPageFilters.installFilters
          ),
          intent: transformIntentFiltersToIntentExportParams(
            companyPageFilters.intentFilters
          ),
        },
      },
    },
  };
}

function transformIdsToInclusionFilterList(ids: number[]) {
  if (ids.length === 0) return;

  return [{ ids, inclusionMethod: LegacyInclusionMethod.ANY_PRESENT }];
}

function transformIdsToSingleIdFilter(ids: string[] | number[]) {
  if (ids.length === 0) return;

  return { ids };
}
